import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  isCommentsOpen,
  shouldOpenCommentsOnLoad,
  hasNewComments,
  canShowComments,
} from '../../../../../selectors/comments';
import {
  setCommentsOpen,
  setHasNewComments,
  setOpenCommentsOnLoad,
} from '../../../../../redux/actions/comments';
import {
  isSingleVideo,
  getLocale,
  isRTL,
} from '../../../../../selectors/app-settings';
import {
  getSiteUrl,
  getMetaSiteId,
} from '../../../../../redux/hydrated-data/hydrated-data';
import { logBi } from '../../../../../worker/actions/bi';
import { getCurrentSiteUser } from '../../../../../selectors/current-site-user';
import { requestLogin } from '../../../../../utils/auth';
import { notForPreview } from '../../../../../utils/not-for-preview';
import { storeForReload } from '../../../../../utils/reload';
import { isInFullScreen } from '../../../../../selectors/player';
import { setSavedTime } from '../../../../../redux/actions/player/set-saved-time';
import {
  playVideo,
  pauseVideo,
} from '../../../../../redux/actions/player/change-playback-status';
import {
  ANNOTO_CLIENT_ID,
  DEVELOPMENT_ANNOTO_CLIENT_ID,
} from '@wix/wix-vod-constants/env';
import { withHandlersAndState } from '../../../../../containers/withHandlersAndState';

const mapStateToProps = (state, ownProps) => {
  const videoItem = _.get(ownProps, 'videoItem');
  const channelData = _.get(ownProps, 'channelData');
  const currentSiteUser = getCurrentSiteUser(state);
  const metaSiteId = getMetaSiteId(state);

  return {
    isCommentsOpen: isCommentsOpen(state),
    isSingleVideo: isSingleVideo(state),
    shouldOpenCommentsOnLoad: shouldOpenCommentsOnLoad(state),
    hasNewComments: hasNewComments(state),
    locale: getLocale(state),
    siteUrl: getSiteUrl(state),
    currentSiteUser: getCurrentSiteUser(state),
    isInFullScreen: isInFullScreen(state),
    canShowComments: canShowComments(
      state,
      videoItem,
      channelData,
      currentSiteUser,
    ),
    savedTime: state.player.savedTime,
    isRTL: isRTL(state),
    metaSiteId,
  };
};

const mapDispatchToProps = {
  setCommentsOpen,
  setHasNewComments,
  playVideo,
  pauseVideo,
  setSavedTime,
  setOpenCommentsOnLoad,
  logBi,
  requestLogin,
  storeForReload,
};

class AnnotoHandlers extends React.Component {
  static propTypes = {
    player: PropTypes.object.isRequired,
    isCommentsOpen: PropTypes.bool.isRequired,
    setCommentsOpen: PropTypes.func.isRequired,
    setHasNewComments: PropTypes.func.isRequired,
    isSingleVideo: PropTypes.bool.isRequired,
    shouldOpenCommentsOnLoad: PropTypes.bool.isRequired,
    hasNewComments: PropTypes.bool.isRequired,
    channelData: PropTypes.object.isRequired,
    videoItem: PropTypes.object.isRequired,
    mobileMode: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    siteUrl: PropTypes.string.isRequired,
    currentSiteUser: PropTypes.object,
    controllerState: PropTypes.object,
    handlers: PropTypes.object,
    isInFullScreen: PropTypes.bool.isRequired,
    canShowComments: PropTypes.bool.isRequired,
    playVideo: PropTypes.func.isRequired,
    pauseVideo: PropTypes.func.isRequired,
    setSavedTime: PropTypes.func.isRequired,
    setOpenCommentsOnLoad: PropTypes.func.isRequired,
    savedTime: PropTypes.number,
    isRTL: PropTypes.bool,
    metaSiteId: PropTypes.string,
  };

  componentDidMount() {
    const {
      player,
      mobileMode,
      locale,
      siteUrl,
      currentSiteUser,
      isRTL,
      metaSiteId,
      handlers,
    } = this.props;

    const debug = process.env.NODE_ENV === 'development';

    player.configureAnnoto({
      clientId: debug ? DEVELOPMENT_ANNOTO_CLIENT_ID : ANNOTO_CLIENT_ID,
      locale,
      ssoAuthRequestHandle: this.handleLoginRequest,
      mediaDetailsHandle: this.getMediaDetails,
      metadataChangeCallback: this.handleMetadataChange,
      uxEventCallback: this.handleUxEvent,
      mediaIdGetter: this.getVideoId,
      rtl: isRTL,
      deviceDetector: {
        isSmallScreen: () => mobileMode,
        isPhone: () => mobileMode,
        isTablet: () => mobileMode,
        isDesktop: () => !mobileMode,
      },
      originProvider: {
        getPageUrl: () => siteUrl,
      },
    });

    if (currentSiteUser) {
      handlers.requestAnnotoToken(currentSiteUser.id, metaSiteId);
    }

    this.updatePlayerTime();
    this.changeAnnotoState();
  }

  componentDidUpdate(prevProps) {
    const {
      isCommentsOpen,
      player,
      canShowComments,
      savedTime,
      controllerState,
    } = this.props;

    const annotoToken = controllerState?.annotoToken;

    if (prevProps.canShowComments !== canShowComments) {
      this.changeAnnotoState();
    }

    if (isCommentsOpen && !prevProps.isCommentsOpen) {
      player.showAnnoto();
      this.logShownBi(true);
    }

    if (prevProps.savedTime !== savedTime) {
      this.updatePlayerTime();
    }

    if (annotoToken && annotoToken !== prevProps.controllerState?.annotoToken) {
      player.setAnnotoAuthToken(annotoToken);
    }
  }

  changeAnnotoState() {
    const { player, canShowComments } = this.props;

    if (canShowComments) {
      player.enableAnnoto();
    } else {
      player.disableAnnoto();
    }
  }

  // show/hide events
  handleUxEvent = ({ name }) => {
    const { setCommentsOpen } = this.props;

    if (name === 'widget:hide') {
      setCommentsOpen(false);
      this.logShownBi(false);
    }

    if (name === 'widget:show') {
      setCommentsOpen(true);
      this.logShownBi(true);
    }
  };

  logShownBi = (isShown) => {
    const { isSingleVideo, videoItem, channelData } = this.props;
    this.props.logBi('widget.vid.showComment.shown', {
      isShown,
      isSingleVideo,
      channelID: channelData.id,
      videoID: videoItem.id,
      source: videoItem.videoSource,
    });
  };

  // metadata update contains info about unread messages
  handleMetadataChange = ({ comments }) => {
    const {
      videoItem,
      channelData,
      isSingleVideo,
      shouldOpenCommentsOnLoad,
      setCommentsOpen,
      hasNewComments,
      setHasNewComments,
    } = this.props;

    const isNewCommentsAdded = comments.countDiff > 0;

    if (isNewCommentsAdded && !hasNewComments) {
      this.props.logBi('widget.vid.newComment.added', {
        isSingleVideo,
        channelID: channelData.id,
        videoID: videoItem.id,
        source: videoItem.videoSource,
      });
    }

    setHasNewComments(isNewCommentsAdded);

    if (shouldOpenCommentsOnLoad) {
      setCommentsOpen(true);
    }
  };

  // used to uniquely identify video in Annoto
  getVideoId = () => this.props.videoItem.id;

  getMediaDetails = () => {
    const { videoItem, channelData } = this.props;

    return {
      title: _.get(videoItem, 'title', ''),
      description: _.get(videoItem, 'description', ''),
      group: {
        id: channelData.id,
        title: _.get(channelData, 'title', ''),
        description: _.get(channelData, 'description', ''),
      },
    };
  };

  handleLoginRequest = () => {
    this.makeLoginRequest();
    return Promise.resolve();
  };

  makeLoginRequest = notForPreview(() => {
    const {
      player,
      currentSiteUser,
      isSingleVideo,
      videoItem,
      channelData,
      isInFullScreen,
      metaSiteId,
      handlers,
    } = this.props;

    if (currentSiteUser) {
      handlers.requestAnnotoToken(currentSiteUser.id, metaSiteId);
    } else {
      this.savePlayerTime();

      if (isInFullScreen) {
        player.exitFullScreen();
      }
      this.props.requestLogin();
    }

    this.props.logBi('widget.vid.commentsLogin.requested', {
      isSingleVideo,
      channelID: channelData.id,
      videoID: videoItem.id,
      source: videoItem.videoSource,
    });
  });

  savePlayerTime() {
    const { player } = this.props;

    this.props.storeForReload({
      savedPlayerTime: player.getCurrentTime(),
    });
  }

  updatePlayerTime() {
    const {
      player,
      pauseVideo,
      playVideo,
      savedTime,
      videoItem,
      setSavedTime,
      setOpenCommentsOnLoad,
    } = this.props;

    if (!player || !savedTime) {
      return;
    }

    // restoring playback to where user stopped before login from Annoto
    player.seekTo(savedTime);
    setOpenCommentsOnLoad(true);
    setSavedTime(0);
    playVideo(videoItem.id);
    pauseVideo(videoItem.id);
  }

  render() {
    return null;
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withHandlersAndState(AnnotoHandlers));
